import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { BrainwaveControlPoint } from '../../utils/audio/BrainwaveOscillatorNode';
import { BrainwaveSettings } from '../../hooks/useWebAudio';
import { BrainwavePointContextMenu } from './BrainwavePointContextMenu';
import { setupCanvasForHighDPI } from './canvasUtils';

const TrackContainer = styled.div`
  position: relative;
  height: 80px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
`;

const CanvasContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;

const CanvasPositioner = styled.div.attrs<{ $left: number }>(props => ({
  style: {
    left: `${props.$left}px`
  }
}))`
  position: absolute;
  top: 0;
  bottom: 0;
`;

const TrackCanvas = styled.canvas`
  display: block;
`;

const ControlPointElement = styled.div<{ $active?: boolean }>`
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => props.$active ? '#ff4081' : '#e91e63'};
  border: 2px solid #ffffff;
  transform: translate(-50%, -50%);
  cursor: grab;
  z-index: 10;
  transition: transform 0.1s;

  &:hover {
    transform: translate(-50%, -50%) scale(1.2);
  }

  &:active {
    cursor: grabbing;
  }
`;

const ControlPointTooltip = styled.div<{ $position?: 'top' | 'bottom'; $x?: number }>`
  position: absolute;
  ${props => props.$position === 'bottom' ? 'top: 100%;' : 'bottom: 100%;'}
  left: 50%;
  transform: ${props => {
    // Adjust horizontal position for edge cases
    if (props.$x !== undefined) {
      if (props.$x < 15) {
        return 'translateX(0)';  // Move right for left edge
      } else if (props.$x > 85) {
        return 'translateX(-100%)';  // Move left for right edge
      }
    }
    return 'translateX(-50%)';  // Center for normal cases
  }};
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
  pointer-events: none;
  ${props => props.$position === 'bottom' ? 'margin-top: 4px;' : 'margin-bottom: 4px;'}
  z-index: 1000;
`;


interface BrainwaveTrackLaneProps {
  trackId: string;
  settings: BrainwaveSettings;
  duration: number;
  pixelsPerSecond: number;
  startTime?: number;
  visibleStartTime?: number;
  visibleEndTime?: number;
  onUpdateSettings: (trackId: string, settings: BrainwaveSettings) => void;
  onUpdateTrack?: (trackId: string, updates: { duration?: number; trimmedDuration?: number }) => void;
  isDragging?: boolean; // Track if parent component is dragging the whole box
}

export const BrainwaveTrackLane: React.FC<BrainwaveTrackLaneProps> = ({
  trackId,
  settings,
  duration,
  pixelsPerSecond,
  startTime = 0,
  visibleStartTime = 0,
  visibleEndTime = duration,
  onUpdateSettings,
  onUpdateTrack,
  isDragging: isParentDragging = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedPoint, setSelectedPoint] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; pointIndex: number } | null>(null);
  const dragStartRef = useRef<{ x: number; y: number; point: BrainwaveControlPoint } | null>(null);
  const justFinishedDraggingRef = useRef(false);
  const isDraggingRightmostRef = useRef(false);
  const lastRenderRef = useRef<{
    startTime: number;
    endTime: number;
    pixelsPerSecond: number;
    canvasLeft: number;
    controlPointsHash: string;
  } | null>(null);
  const [canvasLeft, setCanvasLeft] = useState(0);

  // Helper functions to identify special points
  const getLeftmostPointIndex = useCallback(() => {
    const sortedPoints = [...settings.controlPoints].sort((a, b) => a.time - b.time);
    const leftmostTime = Math.min(...sortedPoints.map(p => p.time));
    return settings.controlPoints.findIndex(p => p.time === leftmostTime);
  }, [settings.controlPoints]);

  const getRightmostPointIndex = useCallback(() => {
    const sortedPoints = [...settings.controlPoints].sort((a, b) => a.time - b.time);
    const rightmostTime = Math.max(...sortedPoints.map(p => p.time));
    return settings.controlPoints.findIndex(p => p.time === rightmostTime);
  }, [settings.controlPoints]);

  const isLeftmostPoint = useCallback((index: number) => {
    return index === getLeftmostPointIndex();
  }, [getLeftmostPointIndex]);

  const isRightmostPoint = useCallback((index: number) => {
    return index === getRightmostPointIndex();
  }, [getRightmostPointIndex]);


  // Update boundary points when startTime or duration changes
  const prevDurationRef = useRef(duration);
  const prevStartTimeRef = useRef(startTime);
  
  useEffect(() => {
    if (!isDraggingRightmostRef.current) {
      const leftIndex = getLeftmostPointIndex();
      const rightIndex = getRightmostPointIndex();
      
      if (leftIndex === -1 || rightIndex === -1) return;
      
      const newEndTime = startTime + duration;
      const durationChanged = prevDurationRef.current !== duration;
      const startTimeChanged = prevStartTimeRef.current !== startTime;
      
      prevDurationRef.current = duration;
      prevStartTimeRef.current = startTime;
      
      if (!durationChanged && !startTimeChanged) return;
      
      const newPoints = [...settings.controlPoints];
      let needsUpdate = false;

      // Handle startTime changes (move leftmost point)
      if (startTimeChanged && newPoints[leftIndex].time !== startTime) {
        newPoints[leftIndex] = { ...newPoints[leftIndex], time: startTime };
        needsUpdate = true;
      }

      // Handle duration changes (box resize)
      if (durationChanged) {
        // Remove points that are now outside the new duration (but keep boundary points)
        const filteredPoints = newPoints.filter((point, index) => 
          index === leftIndex || index === rightIndex || point.time < newEndTime
        );
        
        // Update the rightmost point to the new end time
        const rightmostInFiltered = filteredPoints.findIndex((_, index) => index === rightIndex || filteredPoints[index] === newPoints[rightIndex]);
        if (rightmostInFiltered !== -1) {
          filteredPoints[rightmostInFiltered] = {
            ...filteredPoints[rightmostInFiltered], 
            time: newEndTime
          };
        }

        // Only update if we actually removed points
        if (filteredPoints.length !== newPoints.length) {
          const newSettings: BrainwaveSettings = {
            ...settings,
            controlPoints: filteredPoints.sort((a, b) => a.time - b.time)
          };
          onUpdateSettings(trackId, newSettings);
          return;
        }
        
        needsUpdate = true;
      }

      // Update rightmost point to match duration (if not already updated above)
      if (newPoints[rightIndex].time !== newEndTime) {
        newPoints[rightIndex] = { ...newPoints[rightIndex], time: newEndTime };
        needsUpdate = true;
      }

      if (needsUpdate) {
        const newSettings: BrainwaveSettings = {
          ...settings,
          controlPoints: newPoints.sort((a, b) => a.time - b.time)
        };
        onUpdateSettings(trackId, newSettings);
      }
    }
  }, [startTime, duration]); // Removed settings from dependencies to prevent infinite loop

  // Calculate the frequency range with padding
  const getFrequencyRange = useCallback(() => {
    if (settings.controlPoints.length === 0) {
      return { min: 0.5, max: 40 };
    }
    
    const frequencies = settings.controlPoints.map(p => p.brainwaveFreq);
    const minFreq = Math.min(...frequencies);
    const maxFreq = Math.max(...frequencies);
    
    // Add 20% padding to the range for better visualization
    const range = maxFreq - minFreq;
    const padding = range * 0.2;
    
    // If range is very small (< 1Hz), add at least 0.5Hz padding
    const effectivePadding = Math.max(padding, 0.5);
    
    // Calculate ideal range with padding
    const idealMin = minFreq - effectivePadding;
    const idealMax = maxFreq + effectivePadding;
    
    // Constrain to absolute limits and adjust the other bound if needed
    let actualMin = Math.max(0.5, idealMin);
    let actualMax = Math.min(40, idealMax);
    
    // If we hit the floor/ceiling, ensure we still have reasonable range
    if (actualMin === 0.5 && (actualMax - actualMin) < 2) {
      actualMax = Math.min(40, actualMin + 2);
    }
    if (actualMax === 40 && (actualMax - actualMin) < 2) {
      actualMin = Math.max(0.5, actualMax - 2);
    }
    
    return {
      min: actualMin,
      max: actualMax
    };
  }, [settings.controlPoints]);

  // Calculate buffer for smooth scrolling
  const getBufferTime = useCallback((visibleDuration: number) => {
    const baseBuffer = visibleDuration * 4; // Use 4x visible duration as buffer
    return Math.round(baseBuffer / 5) * 5; // Round to 5-second increments
  }, []);

  // Draw the brainwave line with buffering
  const drawBrainwaveLine = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || settings.controlPoints.length === 0) return;

    // Check if track intersects with visible timeline
    const trackEndTime = startTime + duration;
    if (trackEndTime < visibleStartTime || startTime > visibleEndTime) {
      return;
    }

    const visibleDuration = visibleEndTime - visibleStartTime;
    const bufferTime = getBufferTime(visibleDuration);
    const lastRender = lastRenderRef.current;
    
    // Create a hash of control points to detect changes
    const controlPointsHash = JSON.stringify(
      settings.controlPoints.map(p => `${p.time}-${p.brainwaveFreq}-${p.carrierFreq}-${p.amplitude}`)
    );
    
    // Determine if we need to update the canvas
    let renderStartTime: number;
    let renderEndTime: number;
    let needsUpdate = false;
    
    renderStartTime = Math.max(startTime, visibleStartTime - bufferTime);
    renderEndTime = Math.min(trackEndTime, visibleEndTime + bufferTime);

    if (!lastRender || 
        lastRender.pixelsPerSecond !== pixelsPerSecond ||
        lastRender.controlPointsHash !== controlPointsHash) {


      needsUpdate = true;
      console.log('control points changed or zoom changed');
    } else {
      const leftBoundary = lastRender.startTime 
      const rightBoundary = lastRender.endTime;
      
      // Check if visible region moved outside buffer
      if((visibleStartTime < leftBoundary && leftBoundary.toFixed(2) !== (startTime).toFixed(2)) || 
      (rightBoundary < visibleEndTime && rightBoundary.toFixed(2) !== (duration + startTime).toFixed(2))){
        console.log('visible region moved outside buffer', visibleStartTime, leftBoundary, visibleEndTime, rightBoundary);
        console.log('lastRender.endTime', lastRender.endTime.toFixed(2), (duration + startTime).toFixed(2));
        needsUpdate = true;

      }else{
      // Keep existing stable render region
      renderStartTime = lastRender!.startTime;
      renderEndTime = lastRender!.endTime;
      }
    }
    
    // Only skip rendering if we have a valid last render with the same region, zoom, AND control points
    if (!needsUpdate && lastRender && 
        lastRender.startTime === renderStartTime && 
        lastRender.endTime === renderEndTime &&
        lastRender.controlPointsHash === controlPointsHash) {
          //console.log('no redraw needed');
      return; // No redraw needed
    }
    
    const renderDuration = renderEndTime - renderStartTime;
    const maxCanvasWidth = 32768; // Browser limit
    const idealWidth = Math.round(renderDuration * pixelsPerSecond);
    const canvasWidth = Math.min(maxCanvasWidth, idealWidth);
    const canvasHeight = 80; // Height of the track
    
    // Set up canvas with high DPI support
    const ctx = setupCanvasForHighDPI(canvas, canvasWidth, canvasHeight);
    
    // Clear canvas
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    // Sort control points by time
    const sortedPoints = [...settings.controlPoints].sort((a, b) => a.time - b.time);
    
    // Get the actual frequency range
    const freqRange = getFrequencyRange();
    const freqSpan = freqRange.max - freqRange.min;

    // Draw the line
    ctx.strokeStyle = '#e91e63';
    ctx.lineWidth = 3;
    ctx.beginPath();

    // Find points that affect the visible region (including just outside)
    const relevantPoints: Array<BrainwaveControlPoint & { 
      index?: number; 
      isVisible?: boolean; 
      clampToStart?: boolean; 
      clampToEnd?: boolean; 
      interpolated?: boolean; 
    }> = [];
    
    for (let i = 0; i < sortedPoints.length; i++) {
      const point = sortedPoints[i];
      
      // Include points within render region
      if (point.time >= renderStartTime && point.time <= renderEndTime) {
        relevantPoints.push({ ...point, index: i, isVisible: true });
      }
      // Include the point just before the render region (for line continuity)
      else if (point.time < renderStartTime && 
               (i === sortedPoints.length - 1 || sortedPoints[i + 1].time >= renderStartTime)) {
        relevantPoints.push({ ...point, index: i, isVisible: false, clampToStart: true });
      }
      // Include the point just after the render region (for line continuity)
      else if (point.time > renderEndTime && 
               (i === 0 || sortedPoints[i - 1].time <= renderEndTime)) {
        relevantPoints.push({ ...point, index: i, isVisible: false, clampToEnd: true });
      }
    }

    // If no points affect this region, try to interpolate
    if (relevantPoints.length === 0 && sortedPoints.length >= 2) {
      // Find the two points that bracket this render region
      let beforePoint = null;
      let afterPoint = null;
      
      for (let i = 0; i < sortedPoints.length - 1; i++) {
        if (sortedPoints[i].time <= renderStartTime && sortedPoints[i + 1].time >= renderEndTime) {
          beforePoint = sortedPoints[i];
          afterPoint = sortedPoints[i + 1];
          break;
        }
      }
      
      if (beforePoint && afterPoint) {
        // Interpolate values at the start and end of the render region
        const startRatio = (renderStartTime - beforePoint.time) / (afterPoint.time - beforePoint.time);
        const endRatio = (renderEndTime - beforePoint.time) / (afterPoint.time - beforePoint.time);
        
        const startFreq = beforePoint.brainwaveFreq + (afterPoint.brainwaveFreq - beforePoint.brainwaveFreq) * startRatio;
        const endFreq = beforePoint.brainwaveFreq + (afterPoint.brainwaveFreq - beforePoint.brainwaveFreq) * endRatio;
        
        relevantPoints.push(
          { time: renderStartTime, brainwaveFreq: startFreq, carrierFreq: beforePoint.carrierFreq, amplitude: beforePoint.amplitude, isVisible: true, interpolated: true },
          { time: renderEndTime, brainwaveFreq: endFreq, carrierFreq: afterPoint.carrierFreq, amplitude: afterPoint.amplitude, isVisible: true, interpolated: true }
        );
      }
    }

    // Draw the line through relevant points
    let hasStarted = false;
    relevantPoints.forEach((point, idx) => {
      // Calculate canvas position, clamping to canvas bounds
      let x = (point.time - renderStartTime) * pixelsPerSecond;
      if (point.clampToStart) x = 0;
      if (point.clampToEnd) x = canvasWidth;
      
      // Map brainwave frequency to canvas height
      const normalizedFreq = (point.brainwaveFreq - freqRange.min) / freqSpan;
      const y = canvasHeight * (1 - (normalizedFreq * 0.9 + 0.05));

      if (!hasStarted) {
        ctx.moveTo(x, y);
        hasStarted = true;
      } else {
        // Draw cubic bezier curve for smooth interpolation
        const prevPoint = relevantPoints[idx - 1];
        let prevX = (prevPoint.time - renderStartTime) * pixelsPerSecond;
        if (prevPoint.clampToStart) prevX = 0;
        if (prevPoint.clampToEnd) prevX = canvasWidth;
        
        const prevNormalizedFreq = (prevPoint.brainwaveFreq - freqRange.min) / freqSpan;
        const prevY = canvasHeight * (1 - (prevNormalizedFreq * 0.9 + 0.05));
        
        const cpx1 = prevX + (x - prevX) * 0.5;
        const cpy1 = prevY;
        const cpx2 = prevX + (x - prevX) * 0.5;
        const cpy2 = y;
        
        ctx.bezierCurveTo(cpx1, cpy1, cpx2, cpy2, x, y);
      }
    });

    ctx.stroke();
    
    // Calculate canvas position
    const newCanvasLeft = Math.round((renderStartTime - startTime) * pixelsPerSecond);
    
    // Update render info
    lastRenderRef.current = {
      startTime: renderStartTime,
      endTime: renderEndTime,
      pixelsPerSecond,
      canvasLeft: newCanvasLeft,
      controlPointsHash
    };
    
    // Update canvas position state
    setCanvasLeft(newCanvasLeft);
  }, [settings.controlPoints, duration, startTime, visibleStartTime, visibleEndTime, pixelsPerSecond, getFrequencyRange, getBufferTime]);

  // Draw brainwave line when needed
  useEffect(() => {
    drawBrainwaveLine();
  }, [drawBrainwaveLine]);

  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging || justFinishedDraggingRef.current || isParentDragging) return;
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Convert click position to absolute timeline time
    const relativeTime = (x / rect.width) * duration; // Time within the track (0 to duration)
    const absoluteTime = startTime + relativeTime; // Absolute timeline position
    const freqRange = getFrequencyRange();
    const normalizedY = 1 - (y / rect.height);
    const brainwaveFreq = normalizedY * (freqRange.max - freqRange.min) + freqRange.min;
    
    // Use the absolute time (already constrained by the calculation above)
    const time = Math.max(startTime, Math.min(startTime + duration, absoluteTime));
    
    // Get interpolated values at this time
    const values = getInterpolatedValues(settings.controlPoints, time);
    
    // Add new control point
    const newPoint: BrainwaveControlPoint = {
      time: Math.round(time * 10) / 10, // Round to 0.1s
      carrierFreq: values.carrierFreq,
      brainwaveFreq: Math.round(brainwaveFreq * 10) / 10, // Round to 0.1Hz
      amplitude: values.amplitude
    };

    const newSettings: BrainwaveSettings = {
      ...settings,
      controlPoints: [...settings.controlPoints, newPoint].sort((a, b) => a.time - b.time)
    };

    onUpdateSettings(trackId, newSettings);
  }, [isDragging, duration, settings, trackId, onUpdateSettings, getFrequencyRange, startTime]);

  const handlePointMouseDown = useCallback((e: React.MouseEvent, pointIndex: number) => {
    e.stopPropagation();
    
    // Close any open context menu
    setContextMenu(null);
    
    setSelectedPoint(pointIndex);
    setIsDragging(true);
    
    // Set flag if dragging the rightmost point
    if (isRightmostPoint(pointIndex)) {
      isDraggingRightmostRef.current = true;
    }
    
    const point = settings.controlPoints[pointIndex];
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      point: { ...point }
    };
  }, [settings.controlPoints, isRightmostPoint]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || selectedPoint === null || !dragStartRef.current) return;
    
    // Debug logging removed for performance
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    
    // Convert deltas to time and frequency changes using dynamic range
    const freqRange = getFrequencyRange();
    const timeChange = (deltaX / rect.width) * duration;
    const freqChange = -(deltaY / rect.height) * (freqRange.max - freqRange.min);
    
    // Calculate new values
    let newTime = dragStartRef.current.point.time + timeChange;
    const newFreq = Math.max(0.5, Math.min(40, dragStartRef.current.point.brainwaveFreq + freqChange));
    
    // Apply constraints for boundary points
    if (isLeftmostPoint(selectedPoint)) {
      // Leftmost point is constrained to startTime
      newTime = startTime;
    } else if (isRightmostPoint(selectedPoint)) {
      // Rightmost point can change duration by moving horizontally (unlimited expansion)
      const rawNewDuration = Math.max(0.5, newTime - startTime);
      const newDuration = Math.round(rawNewDuration * 10) / 10; // Round to 0.1s
      newTime = startTime + newDuration;
      
      // Create a copy of all points
      const updatedPoints = [...settings.controlPoints];
      
      // Update the rightmost point position first
      updatedPoints[selectedPoint] = {
        ...updatedPoints[selectedPoint],
        time: newTime,
        brainwaveFreq: Math.round(newFreq * 10) / 10
      };
      
      // Filter out any points that are now beyond the new end time
      // Keep the rightmost point (selectedPoint) and any points before newTime
      const filteredPoints = updatedPoints.filter((point, index) => {
        // Always keep the point we're dragging
        if (index === selectedPoint) return true;
        // Remove points that are at or past the new end time
        return point.time < newTime;
      });
      
      // Trimming debug logging removed for performance
      
      // Update settings with the filtered points
      const newSettings: BrainwaveSettings = {
        ...settings,
        controlPoints: filteredPoints.sort((a, b) => a.time - b.time)
      };
      onUpdateSettings(trackId, newSettings);
      
      // Update the track duration if onUpdateTrack is available
      if (onUpdateTrack && newDuration !== duration) {
        onUpdateTrack(trackId, { trimmedDuration: newDuration });
      }
      
      // Exit early to avoid the normal point update below
      return;
    } else {
      // Regular points are constrained to the timeline bounds
      newTime = Math.max(startTime, Math.min(startTime + duration, newTime));
    }
    
    // Update the point
    const newPoints = [...settings.controlPoints];
    newPoints[selectedPoint] = {
      ...newPoints[selectedPoint],
      time: Math.round(newTime * 10) / 10,
      brainwaveFreq: Math.round(newFreq * 10) / 10
    };

    const newSettings: BrainwaveSettings = {
      ...settings,
      controlPoints: newPoints.sort((a, b) => a.time - b.time)
    };

    onUpdateSettings(trackId, newSettings);
  }, [isDragging, selectedPoint, duration, settings, trackId, onUpdateSettings, onUpdateTrack, getFrequencyRange, startTime, isLeftmostPoint, isRightmostPoint]);

  const handlePointContextMenu = useCallback((e: React.MouseEvent, pointIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      pointIndex
    });
  }, []);

  const handleUpdatePoint = useCallback((pointIndex: number, updates: Partial<BrainwaveControlPoint>) => {
    const newPoints = [...settings.controlPoints];
    newPoints[pointIndex] = {
      ...newPoints[pointIndex],
      ...updates
    };

    const newSettings: BrainwaveSettings = {
      ...settings,
      controlPoints: newPoints.sort((a, b) => a.time - b.time)
    };

    onUpdateSettings(trackId, newSettings);
  }, [settings, trackId, onUpdateSettings]);

  const handleDeletePoint = useCallback((pointIndex: number) => {
    // Don't allow deleting if only 2 points remain
    if (settings.controlPoints.length <= 2) return;
    
    // Don't allow deleting leftmost or rightmost points
    if (isLeftmostPoint(pointIndex) || isRightmostPoint(pointIndex)) return;
    
    const newPoints = settings.controlPoints.filter((_, i) => i !== pointIndex);
    const newSettings: BrainwaveSettings = {
      ...settings,
      controlPoints: newPoints
    };

    onUpdateSettings(trackId, newSettings);
    setSelectedPoint(null);
    setContextMenu(null);
  }, [settings, trackId, onUpdateSettings, isLeftmostPoint, isRightmostPoint]);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
    isDraggingRightmostRef.current = false;
    
    // Set flag to prevent immediate canvas clicks after dragging
    justFinishedDraggingRef.current = true;
    setTimeout(() => {
      justFinishedDraggingRef.current = false;
    }, 50); // 50ms delay to prevent click-after-drag
  }, []);


  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const getFrequencyBand = (freq: number): string => {
    if (freq < 4) return 'Delta';
    if (freq < 8) return 'Theta';
    if (freq < 14) return 'Alpha';
    if (freq < 30) return 'Beta';
    return 'Gamma';
  };

  return (
    <TrackContainer ref={containerRef} onClick={handleCanvasClick}>
      <CanvasContainer>
        <CanvasPositioner $left={canvasLeft}>
          <TrackCanvas ref={canvasRef} />
        </CanvasPositioner>
      </CanvasContainer>
      
      {settings.controlPoints.map((point, index) => {
        // Map point.time relative to track timeline (startTime to startTime + duration)
        const relativeTime = point.time - startTime;
        const x = (relativeTime / duration) * 100; // Percentage within track bounds
        const freqRange = getFrequencyRange();
        const normalizedFreq = (point.brainwaveFreq - freqRange.min) / (freqRange.max - freqRange.min);
        const y = 100 - (normalizedFreq * 90 + 5); // Percentage
        
        // Determine tooltip position to keep it visible
        const tooltipPosition = y < 30 ? 'bottom' : 'top';
        
        // Check if this is a boundary point
        const isBoundaryPoint = isLeftmostPoint(index) || isRightmostPoint(index);
        
        return (
          <ControlPointElement
            key={index}
            $active={selectedPoint === index}
            style={{ left: `${x}%`, top: `${y}%` }}
            onMouseDown={(e) => handlePointMouseDown(e, index)}
            onContextMenu={(e) => handlePointContextMenu(e, index)}
          >
            <ControlPointTooltip $position={tooltipPosition} $x={x}>
              {point.time.toFixed(1)}s | {point.brainwaveFreq.toFixed(1)}Hz ({getFrequencyBand(point.brainwaveFreq)})
              {isBoundaryPoint && (isLeftmostPoint(index) ? ' (Start)' : ' (End)')}
            </ControlPointTooltip>
          </ControlPointElement>
        );
      })}
      
      {contextMenu && (
        <BrainwavePointContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          point={settings.controlPoints[contextMenu.pointIndex]}
          pointIndex={contextMenu.pointIndex}
          isBoundaryPoint={isLeftmostPoint(contextMenu.pointIndex) || isRightmostPoint(contextMenu.pointIndex)}
          onUpdatePoint={handleUpdatePoint}
          onDeletePoint={handleDeletePoint}
          onClose={handleCloseContextMenu}
        />
      )}
    </TrackContainer>
  );
};

// Helper function to get interpolated values at a specific time
function getInterpolatedValues(
  points: BrainwaveControlPoint[], 
  time: number
): { carrierFreq: number; brainwaveFreq: number; amplitude: number } {
  const sortedPoints = [...points].sort((a, b) => a.time - b.time);
  
  if (sortedPoints.length === 0) {
    return { carrierFreq: 200, brainwaveFreq: 10, amplitude: 0.3 };
  }
  
  if (time <= sortedPoints[0].time) {
    return {
      carrierFreq: sortedPoints[0].carrierFreq,
      brainwaveFreq: sortedPoints[0].brainwaveFreq,
      amplitude: sortedPoints[0].amplitude
    };
  }
  
  if (time >= sortedPoints[sortedPoints.length - 1].time) {
    const lastPoint = sortedPoints[sortedPoints.length - 1];
    return {
      carrierFreq: lastPoint.carrierFreq,
      brainwaveFreq: lastPoint.brainwaveFreq,
      amplitude: lastPoint.amplitude
    };
  }
  
  // Find interpolation points
  for (let i = 0; i < sortedPoints.length - 1; i++) {
    const p1 = sortedPoints[i];
    const p2 = sortedPoints[i + 1];
    
    if (time >= p1.time && time <= p2.time) {
      const ratio = (time - p1.time) / (p2.time - p1.time);
      return {
        carrierFreq: p1.carrierFreq + (p2.carrierFreq - p1.carrierFreq) * ratio,
        brainwaveFreq: p1.brainwaveFreq + (p2.brainwaveFreq - p1.brainwaveFreq) * ratio,
        amplitude: p1.amplitude + (p2.amplitude - p1.amplitude) * ratio
      };
    }
  }
  
  return { carrierFreq: 200, brainwaveFreq: 10, amplitude: 0.3 };
}

export default BrainwaveTrackLane;